import type {UserDashboardRecord} from "../../chat-agent/types/chatTypes";

/**
 * Dashboards module: response types and pure mapping from the agent's shapes to
 * gallery items. Kept free of app config / http imports so it is unit-testable.
 */

export type DashboardSource = "hermes" | "mine";

export type LayoutHint = {
    viz: string;
    /** Column width on Superset's 12-column grid (1–12). */
    width: number;
    /** 0-based dashboard row; null when the backend doesn't send it (older Hermes). */
    row?: number | null;
};

/** Raw item from `GET /superset/hermes-dashboards`. */
export type HermesDashboardRecord = {
    id: number | string;
    title: string;
    description: string | null;
    embedded_uuid: string;
    superset_dashboard_id: number;
    data_as_of: string | null;
    version: number;
    chart_count: number;
    layout_hint: LayoutHint[];
    source_label: string | null;
    updated_at: string;
    listed_at: string;
};

/** One card in the gallery, whichever backend it came from. */
export type DashboardItem = {
    source: DashboardSource;
    /** Route id: the Hermes listing id, or the DashGen `dashboard_id`. */
    id: string;
    title: string;
    description: string | null;
    embeddedUuid: string | null;
    supersetDomain: string | null;
    /** Unknown for DashGen dashboards. */
    chartCount: number | null;
    layoutHint: LayoutHint[];
    dataAsOf: string | null;
    sourceLabel: string | null;
    updatedAt: string | null;
    /** DashGen only: originating chat session ("Edit with Atlas"). */
    sessionId: string | null;
    /** DashGen only: numeric id for rename / delete. */
    dashgenId: number | null;
};

export type DashboardList = {
    /** False when the agent answered 404 (Superset disabled) or is not configured. */
    available: boolean;
    supersetDomain: string | null;
    dashboards: DashboardItem[];
};

export const UNAVAILABLE_LIST: DashboardList = {available: false, supersetDomain: null, dashboards: []};

// region mapping
function str(v: unknown): string | null {
    return typeof v === "string" && v.trim() ? v : null;
}

function normalizeLayoutHint(raw: unknown): LayoutHint[] {
    if (!Array.isArray(raw)) return [];
    return raw
        .filter((h): h is {viz?: unknown; width?: unknown; row?: unknown} => !!h && typeof h === "object")
        .map(h => ({
            viz: typeof h.viz === "string" ? h.viz : "",
            width: Number(h.width) || 0,
            row: typeof h.row === "number" && Number.isInteger(h.row) && h.row >= 0 ? h.row : null,
        }));
}

export function mapHermesRecord(raw: HermesDashboardRecord, supersetDomain: string | null): DashboardItem {
    return {
        source: "hermes",
        id: String(raw.id),
        title: str(raw.title) ?? "Untitled dashboard",
        description: str(raw.description),
        embeddedUuid: str(raw.embedded_uuid),
        supersetDomain,
        chartCount: typeof raw.chart_count === "number" ? raw.chart_count : null,
        layoutHint: normalizeLayoutHint(raw.layout_hint),
        dataAsOf: str(raw.data_as_of),
        sourceLabel: str(raw.source_label),
        updatedAt: str(raw.updated_at) ?? str(raw.listed_at),
        sessionId: null,
        dashgenId: null,
    };
}

export function mapDashgenRecord(
    raw: UserDashboardRecord & {superset_domain?: string | null},
    supersetDomain: string | null,
): DashboardItem {
    return {
        source: "mine",
        id: String(raw.dashboard_id),
        title: str(raw.title) ?? "Untitled dashboard",
        description: null,
        embeddedUuid: str(raw.embedded_uuid),
        supersetDomain: str(raw.superset_domain) ?? supersetDomain,
        chartCount: null,
        layoutHint: [],
        dataAsOf: null,
        sourceLabel: null,
        updatedAt: str(raw.updated_at) ?? str(raw.created_at),
        sessionId: str(raw.session_id),
        dashgenId: raw.dashboard_id,
    };
}

/** `{superset_domain, dashboards: [...]}` → mapped list. */
export function normalizeHermesList(data: unknown): DashboardList {
    if (!data || typeof data !== "object" || Array.isArray(data)) {
        return {available: true, supersetDomain: null, dashboards: []};
    }
    const obj = data as {superset_domain?: unknown; dashboards?: unknown};
    const supersetDomain = str(obj.superset_domain);
    const rows = Array.isArray(obj.dashboards) ? obj.dashboards as HermesDashboardRecord[] : [];
    return {available: true, supersetDomain, dashboards: rows.map(r => mapHermesRecord(r, supersetDomain))};
}

/**
 * The DashGen list exists in two shapes:
 * - old: a bare array of records (no Superset domain at all);
 * - new: `{superset_domain, dashboards | items: [...]}` (records may also carry their own `superset_domain`).
 */
export function normalizeDashgenList(data: unknown): DashboardList {
    let rows: unknown[] = [];
    let supersetDomain: string | null = null;
    if (Array.isArray(data)) {
        rows = data;
    } else if (data && typeof data === "object") {
        const obj = data as {superset_domain?: unknown; dashboards?: unknown; items?: unknown};
        supersetDomain = str(obj.superset_domain);
        rows = Array.isArray(obj.dashboards) ? obj.dashboards : Array.isArray(obj.items) ? obj.items : [];
    }
    const dashboards = rows
        .filter((r): r is UserDashboardRecord => !!r && typeof r === "object" && "dashboard_id" in r)
        .map(r => mapDashgenRecord(r, supersetDomain));
    return {available: true, supersetDomain, dashboards};
}

/** `GET /superset/hermes-dashboards/{id}`: either the item with `superset_domain` merged in, or `{superset_domain, dashboard}`. */
export function normalizeHermesDetail(data: unknown): DashboardItem | null {
    if (!data || typeof data !== "object") return null;
    const obj = data as {superset_domain?: unknown; dashboard?: unknown};
    const supersetDomain = str(obj.superset_domain);
    const raw = (obj.dashboard && typeof obj.dashboard === "object" ? obj.dashboard : obj) as HermesDashboardRecord;
    if (raw.id === undefined || raw.id === null) return null;
    return mapHermesRecord(raw, supersetDomain);
}
// endregion
